import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

// Types
export interface MedicineReminderTime {
  id?: number;
  time: string;  // HH:MM format
  meal_relation: 'before_meal' | 'after_meal' | 'with_meal' | 'empty_stomach';
}

export interface MedicineReminderDocument {
  id: number;
  file: string;
  created_at: string;
}

export interface MedicineReminder {
  id: number;
  medicine_name: string;
  medicine_type: 'tablet' | 'capsule' | 'syrup' | 'drops' | 'injection' | 'inhaler' | 'cream' | 'gel' | 'ointment' | 'powder' | 'spray' | 'patch' | 'other';
  duration_value: number;
  duration_unit: 'day' | 'week' | 'month';
  start_date: string;
  end_date: string;
  frequency_type: 'fixed_times' | 'interval';
  intake_frequency?: 'once' | 'twice' | 'thrice' | 'four_times' | 'five_times' | 'six_times';
  interval_type?: 'every_30_min' | 'hourly' | 'every_2_hours' | 'every_4_hours' | 'every_6_hours' | 'every_8_hours' | 'every_12_hours';
  interval_start_time?: string;
  interval_end_time?: string;
  dosage_value: number;
  dosage_unit: 'tablet' | 'capsule' | 'ml' | 'drops' | 'mg' | 'puff' | 'application' | 'unit';
  doctor_name?: string;
  appointment_reminder_date?: string;
  current_inventory?: number;
  remind_when_inventory?: number;
  medicines_left?: number;
  schedule_times: MedicineReminderTime[];
  documents: MedicineReminderDocument[];
  created_at: string;
  updated_at: string;
}

export interface MedicineReminderPayload {
  medicine_name: string;
  medicine_type: string;
  duration_value: number;
  duration_unit: string;
  start_date: string;
  end_date: string;
  frequency_type: 'fixed_times' | 'interval';
  intake_frequency?: string;
  schedule_times?: { time: string; meal_relation: string }[];
  interval_type?: string;
  interval_start_time?: string;
  interval_end_time?: string;
  dosage_value: number;
  dosage_unit: string;
  doctor_name?: string;
  appointment_reminder_date?: string;
  current_inventory?: number;
  remind_when_inventory?: number;
  medicines_left?: number;
}

export interface MedicineReminderChoices {
  medicine_type: Record<string, string>;
  frequency_type: Record<string, string>;
  intake_frequency: Record<string, string>;
  interval_type: Record<string, string>;
  duration_unit: Record<string, string>;
  dosage_unit: Record<string, string>;
  meal_relation: Record<string, string>;
}

const medicineReminderService = {
  // Get all medicine reminders
  getReminders: async (params?: { start_date?: string; end_date?: string }): Promise<MedicineReminder[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.medicineReminders.base, { params });
    const data = response.data;
    return Array.isArray(data) ? data : data.results || [];
  },

  // Get single reminder
  getReminder: async (id: number): Promise<MedicineReminder> => {
    const response = await axiosInstance.get(`${API_ENDPOINTS.medicineReminders.base}${id}/`);
    return response.data.data || response.data;
  },

  // Create reminder
  createReminder: async (data: MedicineReminderPayload, documents?: File[]): Promise<MedicineReminder> => {
    if (documents && documents.length > 0) {
      const formData = new FormData();
      formData.append('data', JSON.stringify(data));
      documents.forEach(file => formData.append('documents', file));
      const response = await axiosInstance.post(API_ENDPOINTS.medicineReminders.base, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.data;
    }
    const response = await axiosInstance.post(API_ENDPOINTS.medicineReminders.base, data);
    return response.data.data;
  },

  // Update reminder
  updateReminder: async (id: number, data: MedicineReminderPayload, documents?: File[]): Promise<MedicineReminder> => {
    if (documents && documents.length > 0) {
      const formData = new FormData();
      formData.append('data', JSON.stringify(data));
      documents.forEach(file => formData.append('documents', file));
      const response = await axiosInstance.put(`${API_ENDPOINTS.medicineReminders.base}${id}/`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data.data;
    }
    const response = await axiosInstance.put(`${API_ENDPOINTS.medicineReminders.base}${id}/`, data);
    return response.data.data;
  },

  // Delete reminder (soft delete)
  deleteReminder: async (id: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.medicineReminders.base}${id}/`);
  },

  // Upload documents to existing reminder
  addDocuments: async (reminderId: number, files: File[]): Promise<MedicineReminderDocument[]> => {
    const formData = new FormData();
    files.forEach(file => formData.append('documents', file));
    const response = await axiosInstance.post(
      `${API_ENDPOINTS.medicineReminders.base}${reminderId}/documents/`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },

  // Delete document
  deleteDocument: async (reminderId: number, documentId: number): Promise<void> => {
    await axiosInstance.delete(`${API_ENDPOINTS.medicineReminders.base}${reminderId}/documents/delete/`, {
      data: { document_id: documentId }
    });
  },

  // Get choices for dropdowns
  getChoices: async (): Promise<MedicineReminderChoices> => {
    const response = await axiosInstance.get(`${API_ENDPOINTS.medicineReminders.base}choices/`);
    return response.data;
  },

  // Extract medicine details from a prescription file (PDF/image) using AI + OCR
  extractFromPrescription: async (file: File): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await axiosInstance.post(
      API_ENDPOINTS.medicineReminders.extractFromPrescription,
      formData,
      { timeout: 120000, headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data.data;
  },
};

export default medicineReminderService;
